import type { SvgIconComponent } from '@mui/icons-material';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import { UserRole } from '../types';

export interface NavItem {
  to: string;
  label: string;
  icon?: SvgIconComponent;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

export const isAdminRole = (role?: UserRole) =>
  role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN;

/** Admin screens render inside the sidebar layout; everything else keeps the top bar. */
export const isAdminPath = (pathname: string) =>
  pathname === '/admin' || pathname.startsWith('/admin/');

/**
 * Matches on whole path segments. A plain startsWith would light up
 * "Mechanics" while /admin/mechanics-calendar is open, since one path is a
 * string prefix of the other.
 */
export const isNavActive = (to: string, pathname: string) =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

/**
 * The admin screens, grouped as the sidebar shows them. The top bar flattens
 * the same list, so the two can never drift apart.
 */
export const adminNavSections = (role?: UserRole): NavSection[] => [
  {
    title: 'Workshop',
    items: [
      { to: '/admin/dashboard', label: 'Dashboard', icon: SpaceDashboardOutlinedIcon },
      { to: '/admin/appointments', label: 'Appointments', icon: CalendarMonthOutlinedIcon },
      { to: '/admin/mechanics-calendar', label: 'Calendar', icon: ViewWeekOutlinedIcon },
      { to: '/admin/invoices', label: 'Invoices', icon: ReceiptLongOutlinedIcon },
      { to: '/admin/mechanics', label: 'Mechanics', icon: EngineeringOutlinedIcon },
    ],
  },
  {
    title: 'Parts',
    items: [
      { to: '/admin/inventory', label: 'Inventory', icon: Inventory2OutlinedIcon },
      { to: '/admin/purchase-orders', label: 'Parts orders', icon: LocalShippingOutlinedIcon },
    ],
  },
  {
    title: 'Setup',
    items: [
      { to: '/admin/services', label: 'Services', icon: DesignServicesOutlinedIcon },
      { to: '/admin/integrations', label: 'Integrations', icon: HubOutlinedIcon },
      { to: '/admin/shop-settings', label: 'Settings', icon: SettingsOutlinedIcon },
    ],
  },
  // Managing the shops themselves is platform-level, not shop-level.
  ...(role === UserRole.SUPER_ADMIN
    ? [
        {
          title: 'Platform',
          items: [
            { to: '/admin/shops', label: 'Shops', icon: StorefrontOutlinedIcon },
            {
              to: '/admin/platform/integrations',
              label: 'Shop integrations',
              icon: ToggleOnOutlinedIcon,
            },
          ],
        },
      ]
    : []),
];
